import { ColDef } from '@ag-grid-community/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { DatePipe, KeyValue } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Page } from '@bloomreach/spa-sdk';
import { Component as BrComponent } from '@bloomreach/spa-sdk/';
import {
  AnalyticsService,
  DialogService,
  ModalService,
  ResponsiveService,
} from '@frk/eds-components';
import { LiteratureListingBaseComponent } from '@literature/literature-listing-base.component';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { DownloadService } from '@literature/services/download.service';
import { LiteratureAgGridHelperService } from '@literature/services/literature-ag-grid-helper.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { ProfileService } from '@services/profile.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CartActionPerformed,
  LiteratureDocumentListingData,
  ModalId,
} from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LiteratureFundDocumentService } from './services/literature-fund-document.service';
import { FundFilterService } from '@products/fund-listing/services/fund-filter.service';
import { StickyFooterService } from '@services';

const logger = Logger.getLogger('LiteratureFundDocumentComponent');
export interface DownloadLink {
  getDownloadUrl: (url: string) => string;
}

@Component({
  selector: 'ft-literature-fund-document',
  templateUrl: './literature-fund-document.component.html',
  styleUrls: ['./literature-fund-document.component.scss'],
  providers: [LiteratureFundDocumentService, DatePipe],
})
export class LiteratureFundDocumentComponent
  extends LiteratureListingBaseComponent
  implements OnInit, OnDestroy, DownloadLink {
  dataTab = new Map<string, LiteratureDocumentListingData[]>();
  ModalId = ModalId;
  @Input() page!: Page;
  @Input() component!: BrComponent;
  isInternational: boolean;
  private unsubscribe$: Subject<void> = new Subject<void>();
  href: string;
  isColumnLayout = false;
  isPreviewLayout = false;
  @ViewChildren('documentGridColumn')
  documentGridColumn: QueryList<AgGridAngular>;

  constructor(
    private literatureFundDocumentService: LiteratureFundDocumentService,
    private literatureAgGridHelperService: LiteratureAgGridHelperService,
    private router: Router,
    protected changeDetectorRef: ChangeDetectorRef,
    protected translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    protected cartService: CartHandlerService,
    protected dialogService: DialogService,
    protected downloadService: DownloadService,
    protected modalService: ModalService,
    protected formBuilder: FormBuilder,
    protected profileService: ProfileService,
    protected literatureConfig: LiteratureGlobalConfigService,
    protected stickyFooterService: StickyFooterService,
    protected analyticsService?: AnalyticsService,
    protected fundFilterService?: FundFilterService
  ) {
    super(
      responsiveService,
      changeDetectorRef,
      cartService,
      dialogService,
      downloadService,
      translateService,
      formBuilder,
      literatureConfig,
      modalService,
      profileService,
      analyticsService,
      fundFilterService,
      stickyFooterService
    );
  }

  getDownloadUrl(url: string): string {
    return this.literatureFundDocumentService.getDownloadUrl(url);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.href = this.router.url;
    this.isCartConfigured =
      this.literatureFundDocumentService
        .getColumnToDisplay()
        .indexOf('ADD_TO_CART') !== -1;
    const config = this.component?.getModels()?.config;
    this.literatureFundDocumentService
      .register()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        if (state.datatab) {
          state.datatab.forEach((tabData) => {
            tabData.forEach((dataRow: LiteratureDocumentListingData) => {
              const checkDuplicate = tabData.filter(
                (e: LiteratureDocumentListingData) =>
                  e.literatureData.dctermsType ===
                  dataRow.literatureData.dctermsType
              );
              if (checkDuplicate.length > 1) {
                dataRow.literatureData.isDuplicateDCTermsType = true;
              }
            });
          });
        }
        this.dataTab = state.datatab;
        this.populateLiteratureData();
        this.changeDetectorRef.detectChanges();
      });
    this.literatureFundDocumentService.populate({
      fundId: config?.fundId,
      shareClassCode: config?.shareClassCode,
    });
    this.getLayout();
  }
  postEmailDocs(
    recipients: string[],
    litCodes: string[],
    showStatus?: (statusMessage: string) => void
  ): void {
    this.literatureFundDocumentService.postEmailDocs(
      recipients,
      litCodes,
      showStatus
    );
  }
  private populateLiteratureData() {
    this.isInternational = this.literatureFundDocumentService.isInternational;
    this.columnDefs = this.getColumnDefs().filter((coldef: ColDef) => {
      if (
        this.literatureFundDocumentService
          .getColumnToDisplay()
          .indexOf(coldef.colId) !== -1
      ) {
        // to configure column from br
        return true;
      } else {
        return false;
      }
    });
    // Check if MULTI_SELECT_CHECKBOX column exists
    const isMultiSelectCheckbox = this.columnDefs.some(
      (col) => col.colId === 'MULTI_SELECT_CHECKBOX'
    );
    // Modify styles, when 'MULTI_SELECT_CHECKBOX' is not display
    if (!isMultiSelectCheckbox) {
      const title = 'TITLE';
      this.columnDefs.forEach((colDef: any, index) => {
        if (colDef.colId === title && index === 0) {
          colDef.cellStyle = { ...colDef.cellStyle, left: '0px' };
        }
      });
    }
  }

  originalOrder = (
    a: KeyValue<string, []>,
    b: KeyValue<string, []>
  ): number => {
    return 0;
  };

  onSelected(): void {
    // To Avoid double click issue on tabs otherwise it will hide the data on double click.
    if (!this.loaded) {
      this.loaded = false;
    }
  }

  private getColumnDefs(): ColDef[] {
    return this.literatureAgGridHelperService.getFundDocumentListingColDef(
      this.isInternational,
      this.literatureConfig.navigationUrls?.litDetailUrl,
      this.apiDateFormat,
      this,
      this.literatureFundDocumentService.dateFormat,
      this.literatureFundDocumentService.isInvestor(),
      CartActionPerformed.ADDED_FROM_GRID,
      this.isColumnLayout
    );
  }

  /**
   * Get the layout of document from component parameters
   * @returns - boolean
   */
  private getLayout() {
    if (this.component.getParameters().tableView === 'Documents Column View') {
      this.isColumnLayout = true;
    }
    if (this.component.getParameters().tableView === 'Documents Preview') {
      this.isPreviewLayout = true;
    }
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
